/**
 * Centralises card-image CDN URL construction.
 *
 * The CloudFront domain is published per-environment by the
 * `CardImagesCdn` CDK stack as `custom.cardImagesCdnDomain` in
 * `amplify_outputs.json` (see `amplify/backend.ts` and
 * `amplify/storage/cloudfront.ts`). Reading it from the generated
 * outputs means each Amplify branch automatically uses its own
 * distribution — no hardcoded domain to drift across account
 * migrations.
 *
 * The scraper publishes canonical WebP assets at a deterministic path:
 *   https://{cdn}/cards/{series_slug}/{set_slug}/{card_id}.webp
 */

import outputs from '../../amplify_outputs.json';

function readCdnDomain(): string {
  // amplify_outputs.json is a Record<string, unknown> at the type level;
  // narrow defensively because the file may be `{}` on a fresh clone
  // before `ampx generate outputs` runs.
  const custom = (outputs as { custom?: { cardImagesCdnDomain?: unknown } }).custom;
  const domain = custom?.cardImagesCdnDomain;
  if (typeof domain === 'string' && domain.length > 0) return domain;
  return '';
}

const CLOUDFRONT_DOMAIN = readCdnDomain();

// Temporary map until the Card item's `series_slug` field is actually
// read from the GraphQL API. Today the browse grid only has `set_slug`.
const SERIES_SLUG_BY_SET_SLUG: Record<string, string> = {
  sv1: 'scarlet-violet',
};

/**
 * Returns the CDN URL for a representative "card #001" image of the
 * given set. Always returns a string — `CardImage` will gracefully
 * fall back to an inline SVG placeholder if the URL 404s.
 */
export function sampleCardImageUrl(set_slug: string): string {
  if (!CLOUDFRONT_DOMAIN) return '';
  const series = SERIES_SLUG_BY_SET_SLUG[set_slug] ?? 'unknown';
  return `https://${CLOUDFRONT_DOMAIN}/cards/${series}/${set_slug}/${set_slug}_001.webp`;
}

/**
 * URL for the Pokemon card back image used during the reveal animation.
 * Uploaded once by the `card_back` scrape mode in the scraper
 * (`scraper/scripts/upload-cardback.py`), served from the same
 * CloudFront distribution as card fronts at the stable path
 * `card-backs/cardback.webp`.
 */
export const CARD_BACK_IMAGE_URL = CLOUDFRONT_DOMAIN
  ? `https://${CLOUDFRONT_DOMAIN}/card-backs/cardback.webp`
  : '';
